import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * createItemsComponent — render-prop factory for list iteration with
 * per-row identity isolation.
 *
 * The render-isolation stack:
 *
 *    parent page  →  Items   →  RowSubscriber  →  use<Entity>Field
 *     (no sub)     (id-set)     (no sub)         (per-field)
 *
 *   1. Parent page reads only `Items` / `isPending` / `isError` from the
 *      hook result Proxy. Tracked-properties keeps it silent on data
 *      patches.
 *
 *   2. `Items` runs `useQuery({queryKey, enabled: false, select: rowIds})` —
 *      observer-level subscription to the id-set slice. Structural sharing
 *      keeps the result reference stable when the set is unchanged → no
 *      notify → `Items` body doesn't re-render on row patches.
 *
 *   3. `RowSubscriber` does NOT subscribe to data. It's a memo'd identity
 *      wrapper that just plants the `<ListContext.Provider>` value for this
 *      row + renders the consumer's `children({ ref, id })`.
 *
 *   4. Inside the consumer's render fn, leaf components call codegen-emitted
 *      `use<Entity>Field(id, fieldName)` hooks. Each is its own observer on
 *      the same `queryKey`, with a `select` that returns one field's value.
 *      Only the field whose value actually changed notifies → only that
 *      leaf re-renders.
 *
 * Consumer code:
 *
 *     <Items onRowHover={(id) => void prefetchMarket({ pubkey: id })}>
 *       {({ ref, id, intent }) => (
 *         <MarketCard pubkey={id} ref={ref} {...intent} />
 *       )}
 *     </Items>
 *
 *     function MarketCard({ pubkey, ref, onMouseEnter, onFocus, onPointerDown }) {
 *       return (
 *         <article ref={ref} onMouseEnter={onMouseEnter} onFocus={onFocus} onPointerDown={onPointerDown}>
 *           <Title pubkey={pubkey} />
 *           <Traded pubkey={pubkey} />
 *         </article>
 *       );
 *     }
 *
 * Omit `onRowHover` to opt out of prefetch — `intent` is still present in
 * the renderer args, but its handlers no-op.
 *
 *     function Title({ pubkey }) {
 *       const value = useGetMarketsField(pubkey, 'title');
 *       return <h3>{value}</h3>;
 *     }
 *
 * Docs alignment:
 *   - https://tanstack.com/query/latest/docs/framework/react/guides/render-optimizations
 *   - https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 *       ("per-component select functions" pattern, codegen-typed)
 */
import { hashKey } from '@tanstack/react-query';
import { memo, useCallback, useContext, useRef } from 'react';
import { useSdkRuntime } from '../../provider/SdkRuntimeContext.js';
import { ListContext } from '../context/ListContext.js';
import { RegistryContext } from '../context/RegistryContext.js';
import { useIntentPrefetch } from '../hooks/useIntentPrefetch.js';
import { useLatestRef } from '../hooks/useLatestRef.js';
import { useListIds } from '../internal/useListIds.js';
/**
 * Build the `Items` render-prop component ONCE per list-hook instance. It
 * reads the current queryKey / observer from `runtimeRef` on every render,
 * so it is never rebuilt when the list args change — see `ItemsRuntime`.
 */
export function createItemsComponent(runtimeRef) {
  return function Items({
    children,
    onRowHover,
    queryKey: explicitQueryKey
  }) {
    const {
      queryKey: runtimeQueryKey,
      rowKey,
      itemObserver,
      onLastRowVisible,
      entityQueryKey,
      entityProjection,
      nestedEntityProjection,
      viewerOverlay,
      viewerArgs,
      viewerScope,
      viewerRelationshipIds
    } = runtimeRef.current;
    const queryKey = explicitQueryKey ?? runtimeQueryKey;
    // Platform adapter — supplies the layout-animated row wrapper
    // (`rowAnimator.Wrapper`). Stable per provider.
    const {
      adapter
    } = useSdkRuntime();
    // Capture the latest `onRowHover` in a ref so a fresh arrow per render
    // doesn't bust `RowSubscriber`'s memo identity. Same useEvent pattern
    // `useIntentPrefetch` itself uses for the prefetch closure.
    const onRowHoverRef = useLatestRef(onRowHover);
    const dispatchHover = useCallback(id => {
      onRowHoverRef.current?.(id);
    }, [onRowHoverRef]);
    // Subscribe to the registry IDENTITY. An auth identity-flip (anon→user
    // on cold load) swaps the registry singleton via `preReplaceCleanup` →
    // `teardownOldRegistry`, and `useItemObserver` mints a fresh
    // `itemObserver` against the new registry. But `<Items>` reads
    // `itemObserver` from `runtimeRef.current` (a ref — no subscription) and
    // otherwise only observes `useListIds`; when the flip leaves the id-set
    // unchanged, `<Items>` never re-renders, so it keeps handing every row
    // the OLD ref bound to the torn-down registry. The rows then stay
    // subscribed to a dead registry and their per-row NATS subjects never
    // reach the live wire — the "feed cards silent on hard refresh, fixed by
    // a route round-trip (which remounts <Items>)" bug. Consuming the context
    // here re-renders `<Items>` on the swap, re-invoking `itemObserver(id)`
    // (now cache-reset + registry-fresh) so each row re-attaches to the live
    // registry. The provider's value is the stable pinned singleton, so this
    // adds zero re-renders during normal operation — only a genuine swap
    // notifies.
    const registry = useContext(RegistryContext);
    // Subscribe to the row-id slice via the shared `useListIds` helper.
    // Same observer that backs the list hook's public `ids` field, so
    // both consumers share one cache-attached observer per `queryKey`.
    const sourceIds = useListIds(queryKey, rowKey);
    const ids = viewerRelationshipIds ?? sourceIds;
    // `ListContext` value, keyed by the queryKey hash. A fresh queryKey
    // array every render must NOT churn the Provider value (every field-hook
    // leaf would re-render); but on a genuine arg change the value DOES
    // update so leaf field hooks re-point to the new cache entry — their
    // `keepPreviousData` then holds the prior field value until it settles.
    const keyHash = hashKey(queryKey);
    const ctxRef = useRef(null);
    if (ctxRef.current === null || ctxRef.current.hash !== keyHash || ctxRef.current.registry !== registry || ctxRef.current.viewerScope !== viewerScope) {
      ctxRef.current = {
        hash: keyHash,
        registry,
        ...(viewerScope !== undefined ? {
          viewerScope
        } : {}),
        value: {
          queryKey,
          rowKey,
          ...(entityQueryKey !== undefined ? {
            entityQueryKey
          } : {}),
          ...(entityProjection !== undefined ? {
            entityProjection
          } : {}),
          ...(nestedEntityProjection !== undefined ? {
            nestedEntityProjection
          } : {}),
          ...(viewerOverlay !== undefined ? {
            viewerOverlay
          } : {}),
          ...(viewerArgs !== undefined ? {
            viewerArgs
          } : {}),
          ...(viewerScope !== undefined ? {
            viewerScope
          } : {})
        }
      };
    }
    const currentContext = ctxRef.current;
    if (currentContext === null) {
      throw new Error('Items context initialization failed');
    }
    const contextValue = currentContext.value;
    // Layout-animated per-row wrapper. `<Items>` wraps each rendered row
    // in `adapter.rowAnimator.Wrapper` so that when a realtime patch live
    // re-ranks a sorted list (manifest `sort:` → `PagedData._sort` → the
    // codegen-emitted `resortRows`), `ids` reorders and the row's
    // position change ANIMATES instead of jumping. On web the wrapper is
    // a `<motion.div layout>` (honouring `prefers-reduced-motion`); on RN
    // it is a passthrough. Routed via the adapter so `motion/react`
    // (web-only) never enters core / RN bundles.
    // The slot's component is typed with `unknown` props/return in
    // `adapter.ts` (so that module stays free of a React type import and
    // one slot satisfies both platforms). Narrow to the local
    // `ReactNode`-typed shape here at the single core consumption point.
    const RowAnimator = adapter.rowAnimator.Wrapper;
    return _jsxs(ListContext.Provider, {
      value: contextValue,
      children: [ids.map(id => _jsx(RowSubscriber, {
        id: id,
        observerRef: itemObserver(id),
        render: children,
        animator: RowAnimator,
        dispatchHover: dispatchHover
      }, id)), onLastRowVisible !== undefined && ids.length > 0 && _jsx(LoadMoreSentinel, {
        onVisible: onLastRowVisible
      }, `sentinel:${ids[ids.length - 1]}`)]
    });
  };
}
// ─── Auto-load-more sentinel ────────────────────────────────────────────────
/**
 * Renders a zero-height marker div with an IntersectionObserver. When the
 * marker enters the viewport, fires `onVisible` once per mount. Keying the
 * sentinel on the current last-row id (from `<Items>`) means a new page's
 * arrival remounts a fresh sentinel for the new tail — automatic re-arm.
 *
 * Lives next to `<Items>` rather than inside `RowSubscriber` because
 * composing two ref callbacks on the same row element trips React
 * Compiler's "ref access during render" guard. A separate sibling is
 * structurally cleaner and the IntersectionObserver behaviour is
 * identical — the marker sits at the bottom of the rendered list.
 */
function LoadMoreSentinel(t0) {
  const $ = _c(5);
  const {
    onVisible
  } = t0;
  const {
    adapter
  } = useSdkRuntime();
  let t1;
  if ($[0] !== adapter || $[1] !== onVisible) {
    t1 = el => {
      if (el === null) {
        return;
      }
      let disposed = false;
      const unbind = adapter.visibilityRef.observeElement(el, intersecting => {
        if (!intersecting || disposed) {
          return;
        }
        disposed = true;
        onVisible();
        unbind();
      }, {
        rootMargin: "200px"
      });
      return () => {
        disposed = true;
        unbind();
      };
    };
    $[0] = adapter;
    $[1] = onVisible;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const attach = t1;
  let t2;
  if ($[3] !== attach) {
    t2 = _jsx("div", {
      ref: attach,
      "aria-hidden": true,
      style: {
        height: 1,
        width: "100%",
        gridColumn: "1 / -1"
      }
    });
    $[3] = attach;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
/**
 * One per row. No data subscription, no per-row context — just hands the
 * consumer's render fn an `id` + a stable viewport-observer `ref`, and
 * wraps the result in the platform's layout-animated `animator` so that a
 * live re-rank (`ids` reorder) animates the row's position change. Plain
 * `React.memo` — default shallow equality (`animator` and `dispatchHover`
 * are stable references). Per-leaf re-render isolation comes from
 * TanStack's tracked-properties + per-field `select` (see
 * `createFieldHook`) and consumer-side React Compiler memoization of the
 * row component, not from anything custom here.
 */
function RowSubscriberImpl(props) {
  const $ = _c(9);
  const {
    id,
    observerRef,
    render,
    animator: Animator,
    dispatchHover
  } = props;
  let t0;
  if ($[0] !== dispatchHover || $[1] !== id) {
    t0 = () => dispatchHover(id);
    $[0] = dispatchHover;
    $[1] = id;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const intent = useIntentPrefetch(t0);
  let t1;
  if ($[3] !== Animator || $[4] !== id || $[5] !== intent || $[6] !== observerRef || $[7] !== render) {
    t1 = _jsx(Animator, {
      children: render({
        ref: observerRef,
        id,
        intent
      })
    });
    $[3] = Animator;
    $[4] = id;
    $[5] = intent;
    $[6] = observerRef;
    $[7] = render;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
RowSubscriberImpl.displayName = 'RowSubscriber';
const RowSubscriber = memo(RowSubscriberImpl);